export type BlogPost = {
  slug: string;
  /** H1 displayed at the top of the article. */
  title: string;
  /** <title> tag value (usually slightly different — punchier or includes brand). */
  metaTitle: string;
  /** <meta name="description"> value + summary on the index page. */
  description: string;
  /** Publish date in ISO YYYY-MM-DD form. */
  publishedOn: string;
  /** Read-time estimate, e.g. "8 min read". */
  readingTime: string;
  /** Dek shown under the title. Plain text, no HTML. */
  lede: string;
  /** Article body as a self-contained HTML string. */
  bodyHtml: string;
};

const RASTERIZE_AND_FLATTEN: BlogPost = {
  slug: 'the-rasterize-and-flatten-technique',
  title: 'Why most PDF redaction tools secretly fail (and how to do it right)',
  metaTitle: 'Why most PDF redaction tools secretly fail — anomia',
  description:
    "Drawing a black rectangle on top of a PDF doesn't delete the text underneath — and most redaction tools, including Acrobat, get this wrong. Here's the technique that actually works.",
  publishedOn: '2026-05-08',
  readingTime: '8 min read',
  lede: "A black rectangle on a PDF isn't redaction. It's a sticker. Anyone can peel it off. Here's why — and how anomia handles it differently.",
  bodyHtml: `
    <p>If you draw a black box over text in a PDF, you can recover the underlying text in about ten seconds with a tool called <code>pdftotext</code>. This is true for most PDFs in the wild that claim to be redacted. Independent academic testing of Adobe Acrobat's redaction in published security studies has found failure rates around sixty-five percent: the supposedly-redacted text remains recoverable through copy-paste, text extraction tools, or just opening the file in a different reader.</p>

    <p>It's not because the people producing these documents are careless. It's because PDFs are weirder than they look, and most "redaction" tools don't actually delete the text they're claiming to hide. They cover it up with a sticker.</p>

    <p>Here's why, and what to do about it.</p>

    <h2>a PDF isn't an image</h2>

    <p>When you look at a PDF on screen, your brain registers "a page of text." That's a lie the format tells you. A PDF is actually a list of drawing instructions. Something like:</p>

    <pre><code>move pen to (90, 720)
set font to Helvetica 12pt
draw text: "John Smith"
move pen to (90, 700)
draw text: "SSN: 123-45-6789"
draw rectangle (filled black) at (200, 700) width 100 height 14</code></pre>

    <p>Your PDF reader executes the instructions in order and paints what you see. The black rectangle is drawn <em>after</em> the text, so visually it covers the SSN. But the text instructions are still in the file. Anyone who opens the same PDF in a non-rendering tool — <code>pdftotext</code>, a Python parser, or just copy-paste from many viewers — gets the original text back. The black box is a sticker. The underlying text is undisturbed.</p>

    <h2>how "redaction" usually fails</h2>

    <p>There are roughly three flavors of broken redaction in the wild.</p>

    <p><strong>The annotation method.</strong> A user opens Acrobat, draws a black rectangle over the SSN, and saves. The rectangle becomes a separate annotation object layered on top of the text. Both layers ship in the file. Trivial to undo.</p>

    <p><strong>The change-tracked method.</strong> Some tools mark the text as "redacted" in metadata but leave the actual text in the content stream. The original viewer respects the metadata and hides the text. A different viewer ignores the metadata and shows it.</p>

    <p><strong>The font-trick method.</strong> A few tools change the font of the redacted text to one that renders as invisible characters. Same problem. The underlying string is still in the file. Ctrl+A, Ctrl+C, paste into a plain text editor, and there it is.</p>

    <p>The verification test is brutal:</p>

    <pre><code>pdftotext suspicious.pdf -</code></pre>

    <p>If the output contains the text you tried to redact, the redaction is fake. No exceptions.</p>

    <h2>the technique that actually works</h2>

    <p>The only redaction technique that's robust across every viewer, every parser, and every PDF library is to destroy the text instructions entirely. The accepted way is to <strong>rasterize and flatten</strong>: convert each page to a flat image, draw the redaction rectangles on the image, then re-encode the image back into a new PDF.</p>

    <p>In TypeScript, using <code>pdfjs-dist</code> for parsing and <code>pdf-lib</code> for output:</p>

    <pre><code>import { getDocument } from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';

async function redact(input: ArrayBuffer, rects: Rect[]): Promise&lt;Uint8Array&gt; {
  const src = await getDocument({ data: input }).promise;
  const out = await PDFDocument.create();

  for (let pageNum = 1; pageNum &lt;= src.numPages; pageNum++) {
    const page = await src.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 });

    // 1. Rasterize the page to a canvas.
    const canvas = new OffscreenCanvas(viewport.width, viewport.height);
    const ctx = canvas.getContext('2d')!;
    await page.render({ canvasContext: ctx, viewport }).promise;

    // 2. Draw black rectangles over the PII regions.
    ctx.fillStyle = 'black';
    for (const r of rects.filter((r) =&gt; r.page === pageNum)) {
      ctx.fillRect(r.x * 2, r.y * 2, r.w * 2, r.h * 2);
    }

    // 3. Re-encode the canvas as a JPEG, embed in the new PDF.
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.92 });
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const img = await out.embedJpg(bytes);
    const newPage = out.addPage([viewport.width / 2, viewport.height / 2]);
    newPage.drawImage(img, {
      x: 0, y: 0,
      width: newPage.getWidth(),
      height: newPage.getHeight(),
    });
  }

  return out.save();
}</code></pre>

    <p>That's roughly thirty lines doing three things.</p>

    <ol>
      <li><strong>Rasterize.</strong> Each PDF page becomes a bitmap. The text instructions are gone. There's just pixels.</li>
      <li><strong>Paint over the pixels.</strong> A black rectangle drawn on a canvas is just black pixels. There's no text underneath because there's no text anywhere in this image.</li>
      <li><strong>Re-encode.</strong> Embed the JPEG into a new PDF. The output is a PDF whose only "text" is whatever a future OCR run might extract from the image. The black rectangles aren't transparent overlays. They're part of the image now.</li>
    </ol>

    <p>The trade-off: file size goes up (images are bigger than text), and the output isn't searchable. For redaction, that's the point. If you can search it, you can un-redact it.</p>

    <h2>edge cases worth knowing</h2>

    <p>A few things the simple version above doesn't handle, and that any production redaction tool needs to handle.</p>

    <p><strong>Already-image PDFs.</strong> Some PDFs are scans. A single image per page, no text instructions at all. The text you want to redact is drawn into the pixels of that image. The rasterize step still works (you re-rasterize the rendered page), but you have to OCR the page first to know <em>where</em> to draw the rectangles, since there's no underlying text to query.</p>

    <p><strong>Form fields.</strong> PDF forms can have separate field metadata. Names like <code>SSN</code> or <code>DOB</code> baked into the form definition itself. Even after rasterizing the visible page, the field metadata can persist if you're not careful. Strip the AcroForm tree along with the rasterization.</p>

    <p><strong>Page metadata.</strong> Author, title, software, revision history. None of those touch the visible page, so rasterizing doesn't remove them. They live in the document info dictionary or the XMP metadata stream and have to be wiped explicitly.</p>

    <p><strong>Resolution.</strong> Render at 2x or higher. At 1x, anti-aliased text on the rendered image becomes mush at the zoom levels people commonly read at.</p>

    <h2>verifying redaction worked</h2>

    <p>Run the same test on the output:</p>

    <pre><code>pdftotext redacted.pdf -</code></pre>

    <p>You should get nothing, or at most OCR-derived text from a future tool, which has no relationship to the original. If the original sensitive strings come back, something went wrong and the file is unsafe.</p>

    <p>A more thorough test: open the redacted PDF in Adobe Reader, select all (Ctrl+A), copy, paste into a plain text editor. Empty clipboard means good. Original text means bad.</p>

    <h2>how anomia does it</h2>

    <p>anomia uses rasterize-and-flatten by default for every PDF redaction. There's no "fast mode" that swaps in a faster-but-unsafe alternative. Every redacted PDF you download has been rasterized at 2x scale, painted on, and re-encoded as JPEG. We also strip the document metadata and any AcroForm field definitions in the same pass.</p>

    <p>That's why an anomia-redacted PDF is sometimes <em>bigger</em> than the original. We trade compression for safety. The alternative is the failure mode that produced the sixty-five percent number, and that's not a trade we're willing to make.</p>

    <p>If you'd like to verify it yourself, drop any PDF into <a href="/">anomia.app</a>, redact it, then run <code>pdftotext</code> on the output. The redacted text is gone. Actually gone, not just hidden.</p>

    <p class="blog-post-signoff">— Sean</p>
  `,
};

const VERIFYING_PRIVACY_TOOLS: BlogPost = {
  slug: 'verifying-privacy-tools',
  title: 'How to verify a "privacy tool" isn\'t lying to you',
  metaTitle: 'How to verify a privacy tool isn\'t lying to you — anomia',
  description:
    "Every privacy tool claims your files don't leave your device. Most of them are lying. Here's how to find out for yourself in under a minute.",
  publishedOn: '2026-05-08',
  readingTime: '6 min read',
  lede: "Every privacy tool claims your files don't leave your device. Most of them are lying. Here's how to find out for yourself in under a minute.",
  bodyHtml: `
    <p>Privacy is the easiest claim to make and the hardest to keep. "Your files never leave your device" costs nothing to put in a hero section. The number of tools that put it on their landing page and quietly POST your file to a backend the moment you drop it in is uncomfortably large.</p>

    <p>You don't have to trust the marketing. You can verify in your browser, in under a minute, on any tool that claims to do its work locally.</p>

    <h2>the DevTools network tab method</h2>

    <p>This is the thirty-second test. It works on any web tool that processes files in the browser.</p>

    <ol>
      <li>Open the tool's website.</li>
      <li>Right-click anywhere on the page and choose Inspect (or press F12 / Cmd+Opt+I).</li>
      <li>Switch to the Network tab.</li>
      <li>Click the clear button to wipe out the page-load requests. We don't care about those. Every web app has to load its own code.</li>
      <li>Drop a file into the tool. Process it. Download the result.</li>
      <li>Watch the network tab.</li>
    </ol>

    <p>If anything shows up in that list during processing — a POST to the tool's API, a request with your file in the payload, a "telemetry" beacon — the tool is sending data somewhere. That's not necessarily evil (some tools are honest about needing a server), but it isn't local-only.</p>

    <p>If the network tab stays empty while the tool processes your file, that's a strong signal. Nothing about your file went anywhere. The processing happened on your machine.</p>

    <p>This works because your browser can't hide network requests from itself. If a tool is uploading something, the request is in DevTools.</p>

    <h2>the airplane-mode test</h2>

    <p>The DevTools test tells you nothing went out during this run. The airplane-mode test tells you something stronger: nothing <em>can</em> go out, because there's no network.</p>

    <ol>
      <li>Load the tool's homepage. Wait for it to fully load. Drop zone visible, JavaScript ready.</li>
      <li>Disconnect from the internet. Disable Wi-Fi, unplug the ethernet, turn on airplane mode, whatever you've got.</li>
      <li>Drop your file. Try to process it. Try to download the result.</li>
    </ol>

    <p>A genuinely local tool will keep working. The processing will run, and the download will succeed. Some tools may need a one-time download (a model, fonts) that they cache on first load. After that one-time fetch, they should work offline forever.</p>

    <p>A tool that secretly relies on a server will hang or error out. There's no way for it to call home.</p>

    <p>This is a stronger test than DevTools because it's robust against the rare case where a tool sends data through a sneaky channel — a WebSocket that doesn't show up cleanly in network panels, a request hidden in another tab, a <code>navigator.sendBeacon</code> call that fires on page unload. With no network, none of those work.</p>

    <h2>reading the source</h2>

    <p>For tools that claim to be open source, the next step is reading the code that processes your file. You don't need to be a security researcher. You're looking for one specific thing: any function that takes the file as input and contains the word <code>fetch</code>, <code>XMLHttpRequest</code>, <code>WebSocket</code>, <code>sendBeacon</code>, or any HTTP library call (<code>axios</code>, <code>ky</code>, <code>got</code>).</p>

    <p>Search the repo for those keywords. Open each result. For each one, ask: is this a request that has anything to do with the user's file? Or is it something benign like loading a CSS file, fetching an icon, downloading a model from a CDN?</p>

    <p>The benign requests are usually obvious from context. They happen on page load, they hit a CDN, the URL doesn't include your data. The sketchy requests stand out: they live inside a function called <code>process</code> or <code>upload</code>, and the request body includes the file.</p>

    <p>If a tool is closed source and claims to be local-only, you're back to the network and airplane tests. They aren't as definitive as reading the code, but they're hard to fake.</p>

    <h2>red flags in privacy tool architecture</h2>

    <p>Some patterns are quiet tells that a tool isn't really local.</p>

    <p><strong>Sign-up before processing.</strong> If a tool requires you to create an account to redact a single file, ask why. Local processing doesn't need accounts. Accounts mean a backend, a database, a record of who did what to which file.</p>

    <p><strong>Server-side rendering of results.</strong> If you drop a file and the result appears at a URL on the tool's domain (something like tool.com/results/abc123), the result lives on their server. Which means your file went there too.</p>

    <p><strong>"We delete your file after processing."</strong> This is a confession. They have your file. They're promising not to keep it. Maybe they don't, maybe they do, but the file definitely left your device. A tool that processes locally doesn't need a deletion policy because there's nothing to delete.</p>

    <p><strong>"End-to-end encrypted."</strong> Sometimes legit, often a misuse. End-to-end encryption means encrypted in transit between two endpoints. If one of those endpoints is the tool's server, your data is decrypted on their server before it gets processed. Encryption-in-transit is not the same as not-leaving-your-device.</p>

    <p><strong>Heavy server-side dependencies for "AI" features.</strong> Local AI is real now. Small models run in WebGPU and WebAssembly. But "AI-powered" features that require a server are common because hosted models are easier to ship. If a tool advertises ML detection and the network test flags traffic during processing, the model is running on their server. On your file.</p>

    <h2>try it on anomia</h2>

    <p>Run the network test on <a href="/">anomia.app</a>. Drop a file. Watch the network tab. After the initial app load (code, fonts, the detection model on first use) the request count should stay at zero while you process and download.</p>

    <p>If you ever see a request fire during processing, file an issue. That's a bug we'd want to fix immediately.</p>

    <p class="blog-post-signoff">— Sean</p>
  `,
};

const WHY_I_BUILT_THIS: BlogPost = {
  slug: 'why-i-built-this',
  title: 'my friends were uploading their whole lives to AI tools',
  metaTitle: 'my friends were uploading their whole lives to AI tools — anomia',
  description:
    "The conversation that started anomia, why every existing tool fell short, and the technical shifts that finally made browser-only redaction work.",
  publishedOn: '2026-05-08',
  readingTime: '5 min read',
  lede: "The conversation that started anomia, why every existing tool fell short, and the technical shifts that finally made browser-only redaction work.",
  bodyHtml: `
    <p>A few months ago I was hanging out with friends and the topic of AI tools came up. One of them mentioned she'd been using Claude to help organize her tax docs. She was uploading photos of her 1099s, W-2s, the whole stack. Another friend had been using ChatGPT to help him draft a letter to his landlord and had pasted the entire signed lease. A third was running medical bills through one of them to figure out what he actually owed after insurance.</p>

    <p>None of them thought twice about it. Why would they? The tools made it easy. The answers were good. The friction of redacting anything first was higher than the friction of just dropping the file in.</p>

    <p>I sat with this for a while because I'd been doing it too. We all had. And the more I thought about it, the more it bothered me. We were uploading SSNs, addresses, account numbers, salaries, prescription lists, and the names of every person in our lives, into tools whose privacy guarantees we'd never really inspected. Tools whose terms of service we'd skimmed once during signup. Tools that retain the data for at least 30 days, and longer if anything triggers their abuse review.</p>

    <p>The disconnect was wild. These same friends would tear up a piece of paper before throwing it away if it had their address on it. They'd shred old credit card statements. And they were uploading the same information, in much greater volume, to a chatbot that would store it indefinitely.</p>

    <p>I went looking for a tool that would let them clean files before uploading. There had to be one. There were tools. None of them solved the actual problem.</p>

    <h2>what was already out there</h2>

    <p>The cloud-based redaction tools were the most obvious miss. You upload a sensitive PDF to their server, they redact it, they email you a link. The whole process violates the thing you're trying to protect. The implicit pitch is "trust us with your data so we can hide your data." It collapses on contact with the actual use case.</p>

    <p>The desktop-install tools were better in principle. CamoText, a few others. They run locally, they don't phone home. But the friction is real. To redact one tax form you needed to download a 200 MB installer, agree to a license, watch a setup wizard, and restart your browser to make a system PDF print driver work. By the time you're done, the AI could have answered the question twice and your coffee has gotten cold.</p>

    <p>The browser extensions were closer but limited. They could redact in a Word doc or a Google Doc, but not a file you wanted to upload elsewhere. They also had access to every page you visited, which is its own privacy problem.</p>

    <p>The browser-based tools that claimed to be local mostly weren't. Three of the first five I tested fired off a network request the moment I dropped a file in. (See <a href="/blog/verifying-privacy-tools">how I tested that</a>.) The marketing said "your files stay on your device." The network tab said otherwise.</p>

    <p>What I wanted was a tool with no install, no account, no upload, that worked in thirty seconds, and that could prove its privacy claims to a skeptic. Nothing like that existed.</p>

    <h2>what changed recently</h2>

    <p>Browser-only PII redaction wasn't really possible until a few years ago. Two things made it work.</p>

    <p>The first was small NER models that fit in a browser. Three years ago, the only credible PII detectors were either regex-only (decent at SSNs, useless at names) or cloud APIs (which defeats the point). The releases of small specialized models — OpenAI's Privacy Filter v1, Microsoft's Presidio bindings to ONNX, the various distillations of LLM-based entity extractors — changed that. A 1.5 billion parameter model that detects names, addresses, dates, and a hundred other PII categories now fits comfortably in a quantized ONNX file that a browser can cache.</p>

    <p>The second was WebGPU. Running an NER model in pure WebAssembly was technically possible but slow enough that nobody would use it. WebGPU shipped widely enough in 2024 that browser-side inference on a 1.5B-param model takes a few seconds, not a few minutes. The first time I redacted a multi-page lease entirely on my laptop with no network, I knew this was actually shippable.</p>

    <p>Combined with the boring-but-essential pieces — <code>pdfjs-dist</code> for parsing, <code>pdf-lib</code> for output, BlazeFace for face detection on photos, Tesseract for OCR on scans — the stack closed. You could build a tool that did the work end-to-end in the browser, no server step.</p>

    <h2>what I learned building it</h2>

    <p>Three things that surprised me.</p>

    <p><strong>The hard part isn't detection. It's redaction.</strong> Detecting PII in text is mostly a solved problem if you have a decent model. Redacting it from a PDF without leaving the original text in the file is shockingly hard, because <a href="/blog/the-rasterize-and-flatten-technique">PDFs aren't images</a>. I spent more time on the rasterize-and-flatten pipeline than on any other piece of the codebase.</p>

    <p><strong>The trust story matters more than the technology.</strong> I can build a tool that's perfectly local. If users can't verify it's local, none of the engineering matters. Half the design work was making the privacy claim falsifiable. Airplane-mode it works. Network tab stays empty. Source is structured so an auditor can find the relevant code in thirty seconds.</p>

    <p><strong>Free-tier limits are a UX problem, not a math problem.</strong> The first version had a 3-files-per-day limit. Users hit it on day one and assumed the tool was broken. The second version has a 5-file-lifetime free tier and the modal explicitly says "this is a free trial, buy a license if you want unlimited." Same constraint. Completely different perception.</p>

    <h2>what's next</h2>

    <p>The current version handles PDFs, photos, screenshots, and HEIC images. Detection covers names, addresses, emails, phone numbers, SSNs, credit cards, IBANs, government IDs (US, UK, Canada, India, Brazil), dates, GPS coordinates in EXIF, faces, and the text inside photos via OCR.</p>

    <p>The gaps I'd like to close: better OCR quality on phone shots (the current build misses things at angles), language-specific detection for non-English documents, and eventually a Word and PowerPoint pipeline so people can clean Office files before sharing them.</p>

    <p>If you've ever uploaded something to an AI tool and felt weird about it afterward, or watched a friend do it and wanted to say something, <a href="/">try it</a>. Drop a file. Watch the network tab stay quiet. Verify the privacy claim yourself, in your browser, in thirty seconds.</p>

    <p class="blog-post-signoff">— Sean</p>
  `,
};

export const BLOG_POSTS: readonly BlogPost[] = [
  RASTERIZE_AND_FLATTEN,
  VERIFYING_PRIVACY_TOOLS,
  WHY_I_BUILT_THIS,
];

export const BLOG_POST_BY_SLUG: ReadonlyMap<string, BlogPost> = new Map(
  BLOG_POSTS.map((p) => [p.slug, p] as const),
);
